// ─── productTargets.js — per-product target allocation ─────────────────��────
// Derives individual product targets from the marketplace target using each
// product's pctOfMarket share. Supports manual % overrides stored as
// `productPctOverride_${mktId}_${prodId}` in the month data.
//
// Uses largest-remainder rounding so the sum of product targets exactly
// equals the marketplace target (no off-by-one accumulation).

/**
 * Allocate a marketplace target across its products.
 *
 * @param {number}   mktTarget  — total units for this marketplace this month
 * @param {Array}    products   — products from the marketplace (need .id, .pctOfMarket)
 * @param {string}   mktId      — marketplace id (for override key lookup)
 * @param {object}   monthData  — months[key] object (may contain productPctOverride_*)
 * @returns {Array<{ productId, name, target, pct, isLocked, source }>}
 */
export function allocateProductTargets(mktTarget, products, mktId, monthData = {}) {
  if (!products?.length || mktTarget <= 0) return [];

  const items = products.map(p => {
    const overrideKey = `productPctOverride_${mktId}_${p.id}`;
    const lockedPct = monthData[overrideKey] ?? null;
    return {
      productId: p.id,
      name: p.name || p.id,
      pct: lockedPct != null ? lockedPct / 100 : (p.pctOfMarket || 0),
      isLocked: lockedPct != null,
      source: lockedPct != null ? "manual" : (p.pctOfMarket > 0 ? "share" : "none"),
      groupId: p.groupId || null,
    };
  });

  // Normalize: locked products keep their %, unlocked products share the remainder.
  const lockedSum = items.filter(i => i.isLocked).reduce((s, i) => s + i.pct, 0);
  const unlockedRawSum = items.filter(i => !i.isLocked).reduce((s, i) => s + i.pct, 0);
  const remainder = Math.max(0, 1 - lockedSum);

  for (const item of items) {
    if (!item.isLocked) {
      item.pct = unlockedRawSum > 0 ? (item.pct / unlockedRawSum) * remainder : 0;
    }
  }

  // Largest-remainder rounding: allocate integer units so sum = mktTarget
  const rawAlloc = items.map(i => i.pct * mktTarget);
  const floored = rawAlloc.map(v => Math.floor(v));
  let gap = mktTarget - floored.reduce((s, v) => s + v, 0);
  const remainders = rawAlloc.map((v, i) => ({ i, r: v - floored[i] }));
  remainders.sort((a, b) => b.r - a.r);
  for (let j = 0; j < gap && j < remainders.length; j++) {
    floored[remainders[j].i]++;
  }

  return items.map((item, i) => ({
    ...item,
    target: floored[i],
  }));
}

/**
 * Build the override key for a product's locked %.
 */
export function productPctKey(mktId, prodId) {
  return `productPctOverride_${mktId}_${prodId}`;
}

/**
 * Clear all product % overrides for a marketplace in a given month.
 * Returns a partial update object to spread into monthOps.updateMonth().
 */
export function clearProductOverrides(mktId, products, monthData = {}) {
  const updates = {};
  for (const p of (products || [])) {
    const key = productPctKey(mktId, p.id);
    if (monthData[key] != null) updates[key] = null;
  }
  return updates;
}
